import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { resyncExam, submitExamCorrection } from "./JotSlice";

// Partner Exam Registration Data Operation Policy v1.1, section 6 edit types.
const CORRECTION_TYPES = [
  {
    value: "ordinary",
    label: "Ordinary edit",
    hint: "Not caused by JOT. Only allowed before the D-10 deadline.",
  },
  {
    value: "support_confirmed",
    label: "Support-confirmed correction",
    hint: "Confirmed by support or operations. Allowed until D-5.",
  },
  {
    value: "review_requested",
    label: "Review-requested correction",
    hint: "JOT rejected the data or asked for a correction. Change only what JOT flagged. Allowed until D-5.",
  },
  {
    value: "emergency",
    label: "Emergency exception",
    hint: "Directly affects the candidate's right to take the exam. Must be confirmed by both Astro and JOT.",
  },
];

const CORRECTABLE_FIELDS = [
  { name: "first_name", label: "First Name" },
  { name: "last_name", label: "Last Name" },
  { name: "romaji_name", label: "Romaji Name (Latin letters)" },
  { name: "dob", label: "Date of Birth", type: "date" },
  { name: "email", label: "Email", type: "email" },
  { name: "phone", label: "Phone" },
  { name: "address", label: "Address" },
  { name: "city", label: "City" },
  { name: "state", label: "State" },
  { name: "country", label: "Country" },
  { name: "postal_code", label: "Postal Code" },
];

const SYNC_STATE_BADGES = {
  sent: { label: "Sent to JOT", tone: "success" },
  failed: { label: "Failed — retry needed", tone: "danger" },
  not_sent: { label: "Not sent yet", tone: "secondary" },
};

const ACTION_LABELS = {
  new_registration: "Registration",
  resend: "Re-send",
  correction: "Correction",
  cancel: "Cancellation",
  candidate_sync: "Profile sync",
};

const toInputValue = (value) => (value === null || value === undefined ? "" : String(value));

const initialFieldValues = (details) =>
  CORRECTABLE_FIELDS.reduce((acc, field) => {
    const raw = details?.[field.name];
    acc[field.name] = field.type === "date" && raw ? String(raw).slice(0, 10) : toInputValue(raw);
    return acc;
  }, {});

export default function JotSyncPanel({ details, onChanged }) {
  const dispatch = useDispatch();
  const [showCorrection, setShowCorrection] = useState(false);
  const [busy, setBusy] = useState(false);
  const [correctionType, setCorrectionType] = useState("support_confirmed");
  const [reason, setReason] = useState("");
  const [confirmedBy, setConfirmedBy] = useState("");
  const [fieldValues, setFieldValues] = useState({});

  const applicationId = details?.application_id;
  const syncBadge = SYNC_STATE_BADGES[details?.sync_state] || SYNC_STATE_BADGES.not_sent;
  const history = details?.sync_history || [];
  const selectedType = CORRECTION_TYPES.find((t) => t.value === correctionType);

  const openCorrection = () => {
    setFieldValues(initialFieldValues(details));
    setCorrectionType(details?.partner_status === "rejected" ? "review_requested" : "support_confirmed");
    setReason("");
    setConfirmedBy("");
    setShowCorrection(true);
  };

  const retrySync = () => {
    if (!applicationId) return;
    setBusy(true);
    dispatch(resyncExam(applicationId)).then((res) => {
      setBusy(false);
      if (resyncExam.fulfilled.match(res) && !res.payload?.error) {
        toast.success(res.payload?.message || "Sent to JOT successfully.");
      } else {
        toast.error(res.payload?.message || res.payload || "Unable to send to JOT.");
      }
      onChanged();
    });
  };

  const submitCorrection = () => {
    const original = initialFieldValues(details);
    // Send only the fields that actually changed.
    const fields = Object.keys(fieldValues).reduce((acc, name) => {
      if ((fieldValues[name] || "") !== (original[name] || "")) acc[name] = fieldValues[name];
      return acc;
    }, {});

    if (!Object.keys(fields).length) {
      toast.error("Change at least one field before submitting the correction.");
      return;
    }
    if (!reason.trim()) {
      toast.error("Enter the reason for this correction.");
      return;
    }
    if (correctionType === "emergency" && !confirmedBy.trim()) {
      toast.error("Enter who at JOT confirmed this emergency exception.");
      return;
    }

    setBusy(true);
    dispatch(
      submitExamCorrection({
        id: applicationId,
        data: { correction_type: correctionType, reason, confirmed_by: confirmedBy, fields },
      })
    ).then((res) => {
      setBusy(false);
      if (submitExamCorrection.fulfilled.match(res)) {
        if (res.payload?.error) {
          toast.error(res.payload?.message);
        } else {
          toast.success(res.payload?.message || "Correction saved.");
        }
        setShowCorrection(false);
        onChanged();
      } else {
        toast.error(res.payload || "Unable to submit the correction.");
      }
    });
  };

  return (
    <>
      <div className="row px-4 mt-0 mt-sm-2">
        <div className="col-sm-6 mb-2 mb-sm-0">
          <label className="form-label">Sync State</label>
          <div className="form-control">
            <span className={`badge bg-${syncBadge.tone}`}>{syncBadge.label}</span>
          </div>
        </div>
        <div className="col-sm-6 mb-2 mb-sm-0">
          <label className="form-label">Last Synced / Status Checked</label>
          <input
            className="form-control"
            value={`${details?.last_synced_at || "—"} / ${details?.last_status_checked_at || "—"}`}
            disabled
          />
        </div>
      </div>

      {details?.last_sync_error && (
        <div className="row px-4 mt-2">
          <div className="col-12">
            <div className="alert alert-danger py-2 mb-0">
              <b>Last JOT error:</b> {details.last_sync_error}
            </div>
          </div>
        </div>
      )}

      <div className="row px-4 mt-2">
        <div className="col-12 d-flex gap-2 flex-wrap">
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={openCorrection} disabled={busy || !applicationId}>
            Submit Correction
          </button>
          {details?.sync_state === "failed" && (
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={retrySync} disabled={busy}>
              Retry Sync to JOT
            </button>
          )}
        </div>
      </div>

      {history.length > 0 && (
        <div className="row px-4 mt-3">
          <div className="col-12">
            <label className="form-label">JOT Sync &amp; Correction History</label>
            <div className="table-responsive">
              <table className="table table-sm table-bordered mb-0">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Action</th>
                    <th>Type</th>
                    <th>Fields</th>
                    <th>Reason / Confirmed by</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((row, index) => (
                    <tr key={index}>
                      <td className="text-nowrap">{row.created_at}</td>
                      <td>{ACTION_LABELS[row.action] || row.action}</td>
                      <td>{CORRECTION_TYPES.find((t) => t.value === row.correction_type)?.label || "—"}</td>
                      <td>{row.changed_fields?.length ? row.changed_fields.join(", ") : "—"}</td>
                      <td>
                        {row.reason || "—"}
                        {row.confirmed_by ? ` (JOT: ${row.confirmed_by})` : ""}
                      </td>
                      <td>
                        {row.success ? (
                          <span className="badge bg-success">OK</span>
                        ) : (
                          <span className="badge bg-danger" title={row.error || ""}>Failed</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <Modal show={showCorrection} onHide={() => setShowCorrection(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Submit Correction</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Correction Type</label>
            <select className="form-select" value={correctionType} onChange={(e) => setCorrectionType(e.target.value)}>
              {CORRECTION_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            {selectedType && <small className="text-muted">{selectedType.hint}</small>}
          </div>
          <div className="mb-3">
            <label className="form-label">Reason</label>
            <textarea
              className="form-control"
              rows={2}
              value={reason}
              placeholder={correctionType === "review_requested" ? "JOT's rejection / correction reason" : "Why is this being corrected?"}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
          {correctionType === "emergency" && (
            <div className="mb-3">
              <label className="form-label">Confirmed by (JOT)</label>
              <input
                className="form-control"
                value={confirmedBy}
                placeholder="Who at JOT confirmed it, and when"
                onChange={(e) => setConfirmedBy(e.target.value)}
              />
            </div>
          )}
          <div className="row">
            {CORRECTABLE_FIELDS.map((field) => (
              <div className="col-sm-6 mb-2" key={field.name}>
                <label className="form-label">{field.label}</label>
                <input
                  className="form-control"
                  type={field.type || "text"}
                  value={fieldValues[field.name] || ""}
                  onChange={(e) => setFieldValues({ ...fieldValues, [field.name]: e.target.value })}
                />
              </div>
            ))}
          </div>
          <small className="text-muted">The exam date, time and venue are changed in the main form, not here.</small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCorrection(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submitCorrection} disabled={busy}>
            {busy ? "Submitting..." : "Submit Correction"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
